import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import {
  Webhook as WebhookIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import api, { API_BASE_URL } from "../services/api";

const WebhookManager: React.FC = () => {
  const [webhookUrl, setWebhookUrl] = useState(
    `${API_BASE_URL}/docusign/webhook`
  );
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);

  const handleSetupWebhook = async () => {
    setIsConfiguring(true);
    setMessage(null);

    try {
      const response = await api.post("/docusign/webhook/setup", {
        webhookUrl: webhookUrl,
      });

      if (response.data.manualSetupRequired) {
        setMessage({
          type: "info",
          text: `${response.data.message} Please follow the manual setup instructions below.`,
        });
      } else {
        setMessage({
          type: "success",
          text: `Webhook configured successfully! ${response.data.message}`,
        });
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: `Failed to configure webhook: ${
          error.response?.data?.error || error.message
        }`,
      });
    } finally {
      setIsConfiguring(false);
    }
  };

  const handleTestWebhook = async () => {
    setIsTesting(true);
    setMessage(null);

    try {
      const response = await api.post("/docusign/webhook/test", {
        envelopeId: "test-envelope-" + Date.now(),
      });

      setMessage({
        type: "success",
        text: `Test webhook processed successfully! Result: ${JSON.stringify(
          response.data.result
        )}`,
      });
    } catch (error: any) {
      setMessage({
        type: "error",
        text: `Test webhook failed: ${
          error.response?.data?.error || error.message
        }`,
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Webhook Configuration
      </Typography>

      <Typography variant="body1" color="textSecondary" paragraph>
        Configure DocuSign webhooks to automatically update signature status
        when documents are signed.
      </Typography>

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Webhook Setup
          </Typography>

          <TextField
            fullWidth
            label="Webhook URL"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            helperText="This is the URL DocuSign will call when envelope events occur"
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            startIcon={<WebhookIcon />}
            onClick={handleSetupWebhook}
            disabled={isConfiguring || !webhookUrl}
            sx={{ mr: 2 }}
          >
            {isConfiguring ? "Configuring..." : "Setup Webhook"}
          </Button>

          <Button
            variant="outlined"
            onClick={handleTestWebhook}
            disabled={isTesting}
            color="secondary"
          >
            {isTesting ? "Testing..." : "Test Webhook"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Webhook Information
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Webhook Endpoint"
                secondary="/docusign/webhook (POST)"
              />
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Supported Events"
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Chip label="sent" size="small" sx={{ mr: 1, mb: 1 }} />
                    <Chip
                      label="delivered"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                    <Chip
                      label="completed"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                      color="success"
                    />
                    <Chip
                      label="declined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                      color="error"
                    />
                    <Chip
                      label="voided"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                      color="error"
                    />
                  </Box>
                }
              />
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Automatic Actions"
                secondary={
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    <li>Updates signature status in database</li>
                    <li>Downloads completed documents</li>
                    <li>Saves signed documents to uploads/signed/</li>
                    <li>Updates signed_at timestamp</li>
                  </ul>
                }
              />
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemIcon>
                <ErrorIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Production Notes"
                secondary="For production use, ensure your webhook URL is publicly accessible and uses HTTPS"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WebhookManager;
